import { Controller } from '@hotwired/stimulus';
import {
  submitFeedback,
  updateDocumentUserState,
  registerErrorInRollbar,
  getCsrfToken,
  getDusFromDom,
  vpeChatErrorBlocksFeedback,
  restoreAnswerCommentHTML,
  extractHiddenAnswerCommentHTML,
  extractAnswerCommentSiblingHtml,
  snapshotAnswerCommentSiblingHtml,
  syncAllContinueButtonsForDocument,
  batchSetNodeKVPair,
  executeActionsInDom,
  seemsOffline,
  OFFLINE_AI_NOTICE_BODY,
  removeSpeechToTextToolbarFromContainer,
} from '../utils/case_experience_in_rails';
import { normalizeNodeUid } from '../utils/case_experience/normalize_node_uid';

const RESPONSE_SAFETY_TIMEOUT_MS = 30000;
const FEEDBACK_BAD_PHRASE = 'We had trouble generating a response';
const GENERIC_AI_ERROR_MODAL_MESSAGE =
  'Oops! This feature is experiencing difficulty. Sorry for the hassle. You can still review the answer comment below and continue working through the case.';

const ASSISTANT_LOADING_TEXT = 'Loading AI feedback...';

export default class extends Controller {
  static targets = [
    'visibleUi',
    'responseStream',
    'infoModal',
    'infoModalOkay',
    'userTextarea',
    'userDisplayTextarea',
    'assistantTextarea',
    'inputForm',
    'submitButton',
    'submitPlaceholder',
    'feedbackContainer',
    'feedbackTemplate',
    'ratingButton',
    'feedbackComment',
    'feedbackSubmitButton',
    'errorModal',
    'errorMessage',
    'answerCommentPlaceholder',
    'answerCommentBlockTemplate',
    'submittedButtonTemplate',
    'twoColumnTemplate',
  ];

  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    assistantId: String,
    versionId: Number,
    threadId: String,
    messages: Array,
    feedbackSubmitted: Boolean,
    feedbackUserRating: Number,
    feedbackUserComment: String,
    vpeEnabled: Boolean,
    responseSubmitted: Boolean,
    responseError: Boolean,
    characterLimit: Number,
    boxLines: Number,
  };

  _awaitingAssistantResponse = false;
  _responseError = false;
  _responseSafetyTimeout = null;
  _responseUiCompleted = false;
  _responseStreamObserver = null;
  _selectedRating = 0;
  _feedbackSubmitInFlight = false;
  _genericAiSubmitInFlight = false;
  answerCommentHtml = null;
  _threadIdPersistedToDus = null;

  connect() {
    this.infoModalProceedOnClose = false;
    this._onAfterTurboStreamRender = this.afterTurboStreamRender.bind(this);
    document.addEventListener('turbo:after-stream-render', this._onAfterTurboStreamRender);

    if (this.answerCommentAlreadyRendered()) {
      this.hideSiblingAnswerComment();
    }

    void this.bootstrapSubmittedStateOnConnect();
  }

  async bootstrapSubmittedStateOnConnect() {
    if (this.responseErrorValue) {
      this._responseError = true;
      if (this.isDesignSystemCaseExperience()) {
        this.applyDesignSystemOneColumnErrorFallback();
      } else {
        this.removeTwoColumnLayoutIfPresent();
      }
      this.lockUserInput();
      void this.finalizeAnswerCommentInPlaceholder();
      this.applyErrorChrome({ showErrorModal: false });
      this.syncContinueButtonState();
      return;
    }

    if (!this.responseSubmittedValue) {
      if (this.feedbackSubmittedValue) {
        this.disableFeedbackInputs();
      }
      this.syncContinueButtonState();
      return;
    }

    if (this.isDesignSystemCaseExperience()) {
      const feedbackReady = this.assistantResponseComplete(this.messagesValue);
      if (this.isAssistantErrorResponse(this.messagesValue)) {
        this._responseError = true;
        this.applyDesignSystemOneColumnErrorFallback();
        void this.finalizeAnswerCommentInPlaceholder();
      } else if (!feedbackReady) {
        // Pending remount: keep two-column loading + stream observation (do not treat as error).
        this.revealPreResponseFeedbackBlock();
        if (this.usesTwoColumnLayout()) {
          this.showTwoColumnLayout();
          this.showAssistantLoading();
        }
        this._awaitingAssistantResponse = true;
        this.observeResponseStream();
        this.scheduleResponseSafetyTimeout();
      } else if (!this.element.querySelector('.summary-statement-feedback-grid')) {
        this.showTwoColumnLayout();
      }
    } else if (this.usesTwoColumnLayout()) {
      this.showTwoColumnLayout();
    }
    this.lockUserInput();

    const threadId = this.getResolvedThreadId();
    const shouldSyncFromThread =
      threadId &&
      (!this.messagesValue?.length || !this.assistantResponseComplete(this.messagesValue));

    if (shouldSyncFromThread) {
      await this.syncMessagesToDus();
    }

    if (this.messagesValue?.length) {
      this.populateAssistantFromMessages(this.messagesValue);
    } else if (
      !this._awaitingAssistantResponse &&
      threadId &&
      this.assistantResponsePending(this.messagesValue)
    ) {
      this.showAssistantLoading();
      this._awaitingAssistantResponse = true;
      this.observeResponseStream();
      this.scheduleResponseSafetyTimeout();
    } else if (
      !this._awaitingAssistantResponse &&
      this.assistantResponsePending(this.messagesValue)
    ) {
      this.showAssistantLoading();
    }

    if (this.isAssistantErrorResponse(this.messagesValue)) {
      void this.recoverFromAiResponseError('', { showErrorModal: false });
    } else if (this.assistantResponseComplete(this.messagesValue)) {
      await this.completeResponseUi();
    }

    if (this.feedbackSubmittedValue) {
      this.restoreFeedbackSelectionOnConnect();
      this.disableFeedbackInputs();
    } else {
      this.configureFeedbackSubmitButton();
    }

    this.syncContinueButtonState();
  }

  disconnect() {
    document.removeEventListener('turbo:after-stream-render', this._onAfterTurboStreamRender);
    clearTimeout(this._responseSafetyTimeout);
    this.disconnectResponseStreamObserver();
  }

  afterTurboStreamRender() {
    if (!this.element.isConnected) return;
    requestAnimationFrame(() => {
      if (this.element.isConnected) this.processAssistantStreamUpdate();
    });
  }

  findResponseStreamElement() {
    if (this.hasResponseStreamTarget) return this.responseStreamTarget;
    const nodeUid = this.nodeUidValue;
    if (!nodeUid) return null;
    return this.element.querySelector(`#ai-assistant-text-input-stream-${nodeUid}`);
  }

  findAssistantTextarea() {
    if (this.hasAssistantTextareaTarget) return this.assistantTextareaTarget;
    return this.element.querySelector('[data-ai-assistant-text-input-target="assistantTextarea"]');
  }

  getAssistantTextFromDom() {
    const textarea = this.findAssistantTextarea();
    if (!textarea) return '';
    return (textarea.value || textarea.textContent || '').trim();
  }

  getAssistantSubmissionStageFromDom() {
    const streamEl = this.findResponseStreamElement();
    const marker =
      streamEl?.querySelector('.ai-assistant-submission-stage-marker') ||
      streamEl?.querySelector('[id^="ai-assistant-submission-stage-"]');
    const stage = marker?.dataset?.submissionStage;
    return stage ? String(stage) : null;
  }

  hasValidAssistantContent(domText = this.getAssistantTextFromDom()) {
    return Boolean(
      domText &&
        domText !== ASSISTANT_LOADING_TEXT &&
        !domText.includes(FEEDBACK_BAD_PHRASE),
    );
  }

  processAssistantStreamUpdate() {
    if (!this.element.isConnected) return;
    if (this._responseUiCompleted && !this._responseError) return;

    if (this._responseError || this.responseErrorValue) {
      if (this.isDesignSystemCaseExperience()) {
        this.applyDesignSystemOneColumnErrorFallback();
      }
      return;
    }

    const streamEl = this.findResponseStreamElement();
    if (!streamEl) return;

    const threadIdFromDom = streamEl.dataset.aiAssistantTextInputThreadIdValue;
    if (!threadIdFromDom) {
      const marker = streamEl.querySelector('.ai-assistant-thread-id-marker');
      if (marker?.dataset?.threadId) {
        streamEl.dataset.aiAssistantTextInputThreadIdValue = marker.dataset.threadId;
      }
    }
    const resolvedThreadId = streamEl.dataset.aiAssistantTextInputThreadIdValue;
    if (resolvedThreadId) {
      this.threadIdValue = resolvedThreadId;
      void this.persistThreadIdToDus(resolvedThreadId);
    }

    this.hideInputForm();

    const assistantText = this.getAssistantTextFromDom();
    if (!assistantText || assistantText === ASSISTANT_LOADING_TEXT) return;

    if (this.isAssistantErrorResponse()) {
      void this.recoverFromAiResponseError();
      return;
    }

    this.hideSiblingAnswerComment();
    this.placeAnswerCommentInPlaceholder();
    this.suppressEmptyExternalAnswerCommentPlaceholder();
    this.moveFeedbackBelowAnswerComment();
    void this.onAssistantResponseReady();
  }

  observeResponseStream() {
    this.disconnectResponseStreamObserver();
    const streamEl = this.findResponseStreamElement();
    if (!streamEl) return;

    this._responseStreamObserver = new MutationObserver(() => {
      this.processAssistantStreamUpdate();
    });
    this._responseStreamObserver.observe(streamEl, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  disconnectResponseStreamObserver() {
    if (this._responseStreamObserver) {
      this._responseStreamObserver.disconnect();
      this._responseStreamObserver = null;
    }
  }

  isAssistantErrorResponse(msgs = this.messagesValue) {
    if (this._responseError || this.responseErrorValue) return true;
    if (this._responseUiCompleted) return false;

    const domText = this.getAssistantTextFromDom();
    if (domText && domText !== ASSISTANT_LOADING_TEXT) {
      return domText.includes(FEEDBACK_BAD_PHRASE);
    }

    const list = Array.isArray(msgs) ? msgs : [];
    const assistant = [...list].reverse().find((m) => String(m.role) === 'assistant');
    if (!assistant) return false;
    if (String(assistant.submission_stage) === 'error') return true;
    const assistantText = String(assistant.content || '').trim();
    return assistantText.includes(FEEDBACK_BAD_PHRASE);
  }

  assistantResponseComplete(msgs = this.messagesValue) {
    if (this.isAssistantErrorResponse(msgs)) return false;

    const domStage = this.getAssistantSubmissionStageFromDom();
    const hasValidDomContent = this.hasValidAssistantContent();

    if (domStage) {
      return domStage === 'success' && hasValidDomContent;
    }

    const list = Array.isArray(msgs) ? msgs : [];
    const assistant = [...list].reverse().find((m) => String(m.role) === 'assistant');
    const assistantText = String(assistant?.content || '').trim();
    return (
      assistantText.length > 0 &&
      !assistantText.includes(FEEDBACK_BAD_PHRASE) &&
      String(assistant?.submission_stage) === 'success'
    );
  }

  submitGenericAiComponentMessage(event) {
    event?.preventDefault?.();
    if (!this.vpeEnabledValue || this.responseSubmittedValue) return;

    const content = this.userTextareaTarget?.value?.trim();
    if (!content || !this.versionIdValue) return;

    if (this.hasInfoModalTarget) {
      this.infoModalProceedOnClose = true;
      this.openInfoModal();
      return;
    }

    void this.beginGenericAiComponentSubmit();
  }

  openInfoModal() {
    if (!this.hasInfoModalTarget) return;
    this.openModalElement(this.infoModalTarget, {
      onOutsideDismiss: () => this.cancelInfoModal(),
    });
    requestAnimationFrame(() => {
      if (this.hasInfoModalOkayTarget) {
        this.infoModalOkayTarget.focus({ preventScroll: true });
      }
    });
  }

  closeInfoModal() {
    if (!this.hasInfoModalTarget) return;
    const proceed = this.infoModalProceedOnClose;
    this.infoModalProceedOnClose = false;
    this.closeModalElement(this.infoModalTarget);
    if (!proceed) return;
    void this.beginGenericAiComponentSubmit();
  }

  cancelInfoModal() {
    this.infoModalProceedOnClose = false;
    if (!this.hasInfoModalTarget) return;
    this.closeModalElement(this.infoModalTarget);
  }

  dismissInfoModalFromKeyboard(event) {
    if (!this._isEscapeKey(event)) return;
    if (!this.hasInfoModalTarget || !this.isModalOpen(this.infoModalTarget)) return;

    event.preventDefault();
    event.stopPropagation();
    this.cancelInfoModal();
  }

  dismissErrorModalFromKeyboard(event) {
    if (!this._isEscapeKey(event)) return;
    if (!this.hasErrorModalTarget || !this.isModalOpen(this.errorModalTarget)) return;

    event.preventDefault();
    event.stopPropagation();
    this.closeErrorModal();
  }

  _isEscapeKey(event) {
    return event.key === 'Escape' || event.keyCode === 27;
  }

  isModalOpen(containerTarget) {
    if (!containerTarget) return false;
    if (this.usesLegacyModalDisplay(containerTarget)) {
      return containerTarget.style.display !== 'none';
    }
    return !containerTarget.classList.contains('hidden');
  }

  modalControllerFor(containerTarget) {
    if (!containerTarget) return null;
    const root = containerTarget.closest('[data-controller~="railsui-modal"]');
    if (!root) return null;
    return this.application.getControllerForElementAndIdentifier(root, 'railsui-modal');
  }

  usesLegacyModalDisplay(containerTarget) {
    return (
      containerTarget?.classList.contains('ai-info-modal') ||
      containerTarget?.classList.contains('ai-error-modal')
    );
  }

  openModalElement(containerTarget, { onOutsideDismiss } = {}) {
    if (onOutsideDismiss) {
      this._bindModalOutsideDismiss(containerTarget, onOutsideDismiss);
    }
    const controller = this.modalControllerFor(containerTarget);
    if (controller) {
      controller.show();
      return;
    }
    if (this.usesLegacyModalDisplay(containerTarget)) {
      containerTarget.style.display = 'block';
      return;
    }
    containerTarget.classList.remove('hidden');
    containerTarget.classList.add('aquifer-modal-overlay');
    containerTarget.setAttribute('aria-hidden', 'false');
  }

  closeModalElement(containerTarget) {
    if (!containerTarget) return;
    this._unbindModalOutsideDismiss(containerTarget);
    const controller = this.modalControllerFor(containerTarget);
    if (controller) {
      controller.close();
      return;
    }
    if (this.usesLegacyModalDisplay(containerTarget)) {
      containerTarget.style.display = 'none';
      return;
    }
    containerTarget.classList.add('hidden');
    containerTarget.classList.remove('aquifer-modal-overlay');
    containerTarget.setAttribute('aria-hidden', 'true');
  }

  _bindModalOutsideDismiss(containerTarget, onDismiss) {
    this._unbindModalOutsideDismiss(containerTarget);

    const modalRoot = containerTarget.closest('[data-controller~="railsui-modal"]');
    this._disableRailsuiModalDefaultDismiss(containerTarget, modalRoot);

    this._onModalOutsideClick = (event) => {
      if (!this.isModalOpen(containerTarget)) return;

      const content = modalRoot?.querySelector('[data-railsui-modal-target="content"]');
      if (content?.contains(event.target)) return;
      if (!containerTarget.contains(event.target)) return;

      event.preventDefault();
      event.stopPropagation();
      void onDismiss();
    };

    containerTarget.addEventListener('mousedown', this._onModalOutsideClick, true);
    this._modalOutsideDismissContainer = containerTarget;
  }

  _unbindModalOutsideDismiss(containerTarget) {
    if (
      this._onModalOutsideClick &&
      this._modalOutsideDismissContainer === containerTarget
    ) {
      this._modalOutsideDismissContainer.removeEventListener(
        'mousedown',
        this._onModalOutsideClick,
        true,
      );
    }
    if (this._modalOutsideDismissContainer === containerTarget) {
      this._modalOutsideDismissContainer = null;
    }
    this._onModalOutsideClick = null;
  }

  _disableRailsuiModalDefaultDismiss(containerTarget, modalRoot) {
    const action = containerTarget.getAttribute('data-action') || '';
    const filtered = action
      .split(/\s+/)
      .filter((entry) => entry && !entry.includes('closeWithEsc'))
      .join(' ')
      .trim();

    if (filtered) containerTarget.setAttribute('data-action', filtered);
    else containerTarget.removeAttribute('data-action');

    if (modalRoot) {
      modalRoot.setAttribute('data-railsui-modal-click-outside-value', 'false');
    }

    const railsui = this.modalControllerFor(containerTarget);
    if (railsui) {
      railsui.clickOutsideValue = false;
    }
  }

  _prepareGenericAiModal(containerTarget) {
    const modalRoot = containerTarget.closest('[data-controller~="railsui-modal"]');
    this._disableRailsuiModalDefaultDismiss(containerTarget, modalRoot);
  }

  async beginGenericAiComponentSubmit() {
    const content = this.userTextareaTarget?.value?.trim();
    if (!content || !this.versionIdValue) return;
    if (this._genericAiSubmitInFlight || this.responseSubmittedValue) return;

    this._genericAiSubmitInFlight = true;
    this._awaitingAssistantResponse = true;
    this._responseUiCompleted = false;
    this.scheduleResponseSafetyTimeout();
    this.ensureAnswerCommentStored();
    this.hideSiblingAnswerComment();
    if (this.usesTwoColumnLayout()) {
      this.revealPreResponseFeedbackBlock();
      this.showTwoColumnLayout();
      this.showAssistantLoading();
      this.observeResponseStream();
    }
    this.lockUserInput();
    void this.persistGraded();

    try {
      await this._postGenericAiComponentMessage(content);
    } catch (e) {
      registerErrorInRollbar('AI assistant text input submit failed', { error: e });
      void this.recoverFromAiResponseError(e?.message || 'Network error');
    } finally {
      this._genericAiSubmitInFlight = false;
    }
  }

  async submitWithoutAi(event) {
    event?.preventDefault?.();
    if (this.responseSubmittedValue) return;
    const text = this.userTextareaTarget?.value?.trim();
    if (!text) return;
    await this.persistGraded();
    this.responseSubmittedValue = true;
    this.lockUserInput();
    void this.finalizeAnswerCommentInPlaceholder();
    this.syncContinueButtonState();
  }

  async _postGenericAiComponentMessage(content) {
    const token = getCsrfToken();
    const body = new URLSearchParams();
    body.append('authenticity_token', token);
    body.append('generic_ai_component', '1');
    body.append('node_uid', this.nodeUidValue);
    body.append('document_id', String(this.documentIdValue));
    body.append('box_lines', String(this.boxLinesValue || 5));
    body.append('character_limit', String(this.characterLimitValue || 1000));
    body.append('ai_assistant_message[content]', content);

    const threadId = this.getResolvedThreadId();
    const url = threadId
      ? `/ai_assistant/threads/${encodeURIComponent(String(threadId))}/messages`
      : `/ai_assistant/versions/${encodeURIComponent(String(this.versionIdValue))}/messages`;

    if (!threadId) {
      body.append('context_type', 'generic_ai_component');
    }

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'text/vnd.turbo-stream.html',
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRF-Token': token,
      },
      body,
    });

    if (!res.ok) throw new Error(`Generic AI component message request failed: ${res.status}`);

    const html = await res.text();
    if (window.Turbo?.renderStreamMessage && html?.trim()) {
      await window.Turbo.renderStreamMessage(html);
    }
    requestAnimationFrame(() => {
      this.processAssistantStreamUpdate();
      requestAnimationFrame(() => this.processAssistantStreamUpdate());
    });
  }

  async onAssistantResponseReady() {
    if (this._responseUiCompleted || this._responseError) return;
    if (this.isAssistantErrorResponse()) {
      void this.recoverFromAiResponseError();
      return;
    }
    if (!this.assistantResponseComplete()) return;

    this._responseUiCompleted = true;
    this._awaitingAssistantResponse = false;
    clearTimeout(this._responseSafetyTimeout);
    this.disconnectResponseStreamObserver();
    this.responseSubmittedValue = true;
    await this.completeResponseUi();
    await this.syncMessagesToDus();
  }

  async completeResponseUi() {
    if (this._responseError) return;
    this.revealPreResponseFeedbackBlock();
    await this.finalizeAnswerCommentInPlaceholder();
    this.moveFeedbackBelowAnswerComment();
    this.maybeRevealFeedbackUi();
    this.syncContinueButtonState();
  }

  getAnswerCommentPlaceholder() {
    if (this.hasAnswerCommentPlaceholderTarget) return this.answerCommentPlaceholderTarget;
    return this.element.querySelector(
      '.generic-ai-component-answer-comment, .answer-comment-placeholder',
    );
  }

  answerCommentAlreadyRendered() {
    if (this.isDesignSystemCaseExperience()) {
      return Boolean(
        this.element.querySelector('.generic-ai-component-answer-comment .doc-section-body'),
      );
    }

    const placeholder = this.getAnswerCommentPlaceholder();
    return Boolean(placeholder?.querySelector('.doc-section-body'));
  }

  hideSiblingAnswerComment() {
    const liveAnswerComment = this.findAnswerCommentElement();
    if (!liveAnswerComment) return;

    liveAnswerComment.classList.add(
      'hidden',
      'doc-section--generic-ai-answer-comment-suppressed',
    );
    liveAnswerComment.style.setProperty('display', 'none', 'important');
  }

  moveFeedbackBelowAnswerComment() {
    const placeholder = this.getAnswerCommentPlaceholder();
    if (!placeholder) return;

    this.element
      .querySelectorAll('[data-ai-assistant-text-input-target="feedbackContainer"]')
      .forEach((feedbackEl) => {
        const nodeToMove =
          feedbackEl.closest('.ai-assistant-text-input-post-response-ui') || feedbackEl;
        if (placeholder.nextElementSibling === nodeToMove) return;
        placeholder.insertAdjacentElement('afterend', nodeToMove);
      });
  }

  getAnswerCommentRootFromMemory() {
    this.ensureAnswerCommentStored();
    const html = this.answerCommentHtml || this.getNotesController()?.answerCommentHtml;
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('.answer_comment');
  }

  async persistAnswerCommentRevealed(answerCommentId) {
    const answerCommentUid = normalizeNodeUid(answerCommentId);
    if (!answerCommentUid || !this.documentIdValue || !this.userIdValue) return;

    const actions = [
      {
        type: 'setNodeKVPair',
        arguments: { uid: answerCommentUid, key: 'invisible', value: false },
      },
    ];
    const response = await batchSetNodeKVPair(
      actions,
      this.documentIdValue,
      this.userIdValue,
    );
    if (response) {
      executeActionsInDom(actions, this.application);
    }

    const textInputUid = normalizeNodeUid(this.nodeUidValue);
    const stateUpdate = {
      [answerCommentUid]: {
        invisible: { value: false, updatedAt: Date.now() },
      },
    };
    if (textInputUid) {
      stateUpdate[textInputUid] = {
        genericAiComponentAnswerCommentRevealed: { value: true, updatedAt: Date.now() },
      };
    }

    await updateDocumentUserState({
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      body: {
        document_user_state: {
          state: stateUpdate,
        },
      },
    });
  }

  placeAnswerCommentInPlaceholder() {
    if (this.isDesignSystemCaseExperience() && this.answerCommentAlreadyRendered()) {
      this.hideSiblingAnswerComment();
      return;
    }

    const placeholder = this.getAnswerCommentPlaceholder();
    if (!placeholder) return;

    if (this.isDesignSystemCaseExperience() && this.hasAnswerCommentBlockTemplateTarget) {
      if (!placeholder.querySelector('.generic-ai-component-answer-comment, .doc-section-body')) {
        placeholder.innerHTML = this.answerCommentBlockTemplateTarget.innerHTML;
      }
      placeholder.hidden = false;
      placeholder.style.removeProperty('display');
      this.hideSiblingAnswerComment();
      return;
    }

    if (this.answerCommentAlreadyRendered()) {
      this.hideSiblingAnswerComment();
      return;
    }

    if (placeholder.querySelector('.doc-section-body')) {
      this.hideSiblingAnswerComment();
      return;
    }

    this.ensureAnswerCommentStored();

    const liveAnswerComment = this.findAnswerCommentElement();
    const parsedRoot = this.getAnswerCommentRootFromMemory();
    const answerCommentBody =
      parsedRoot?.querySelector('.doc-section-body') ||
      liveAnswerComment?.querySelector('.doc-section-body') ||
      null;

    if (!answerCommentBody) {
      void this.revealAnswerComment();
      return;
    }

    const answerCommentHeading = `
      <div class="doc-section-header-title">
        <div class="answer-comment-title-container">
          <h4 class="doc-section-header-title-heading" style="font-weight: inherit;">Answer Comment</h4>
        </div>
      </div>
    `;
    placeholder.insertAdjacentHTML('afterbegin', answerCommentHeading);
    placeholder.append(document.importNode(answerCommentBody, true));
    this.hideSiblingAnswerComment();
  }

  suppressEmptyExternalAnswerCommentPlaceholder() {
    const placeholders = this.element.querySelectorAll('.answer-comment-placeholder');
    placeholders.forEach((el) => {
      if (el.querySelector('.doc-section-body')) return;
      el.hidden = true;
      el.style.display = 'none';
    });
  }

  async persistAnswerCommentRevealedIfNeeded() {
    if (!this.answerCommentAlreadyRendered()) return;
    const placeholder = this.getAnswerCommentPlaceholder();
    const answerCommentId =
      placeholder?.dataset?.answerCommentUid || this.resolveAnswerCommentId();
    await this.persistAnswerCommentRevealed(answerCommentId);
  }

  async finalizeAnswerCommentInPlaceholder() {
    this.placeAnswerCommentInPlaceholder();
    this.suppressEmptyExternalAnswerCommentPlaceholder();
    await this.persistAnswerCommentRevealedIfNeeded();
  }

  async recoverFromAiResponseError(errorMessage = '', { showErrorModal = true } = {}) {
    if (this._responseError) return;
    if (this._responseUiCompleted) return;
    if (this.assistantResponseComplete() && !this.isAssistantErrorResponse()) {
      void this.onAssistantResponseReady();
      return;
    }

    this._responseError = true;
    this._awaitingAssistantResponse = false;
    this._responseUiCompleted = true;
    clearTimeout(this._responseSafetyTimeout);
    this.responseSubmittedValue = true;
    this.responseErrorValue = true;

    this.hideSiblingAnswerComment();
    if (this.isDesignSystemCaseExperience()) {
      this.applyDesignSystemOneColumnErrorFallback();
    } else {
      this.removeTwoColumnLayoutIfPresent();
    }
    void this.persistGenericAiComponentResponseError(errorMessage);
    this.applyErrorChrome({ showErrorModal });
    await this.finalizeAnswerCommentInPlaceholder();
    void this.syncMessagesToDus();
    this.syncContinueButtonState();
  }

  applyErrorChrome({ showErrorModal = true } = {}) {
    this.removeFeedbackUiIfPresent();
    this.hideAiFeedbackColumn();

    if (showErrorModal && this.hasErrorModalTarget) {
      if (this.hasErrorMessageTarget) {
        this.errorMessageTarget.innerText = seemsOffline()
          ? OFFLINE_AI_NOTICE_BODY
          : GENERIC_AI_ERROR_MODAL_MESSAGE;
      }
      this.openModalElement(this.errorModalTarget, {
        onOutsideDismiss: () => this.closeErrorModal(),
      });
    }
  }

  hideAiFeedbackColumn() {
    const wrapper = this.element.querySelector('.ai-two-column-wrapper');
    if (!wrapper) return;

    const feedbackSection = wrapper.querySelector('.ai-generated-feedback-section');
    if (feedbackSection) {
      feedbackSection.hidden = true;
      feedbackSection.style.display = 'none';
    } else if (wrapper.querySelectorAll('.summary-statement-feedback-column').length >= 2) {
      const feedbackColumns = wrapper.querySelectorAll('.summary-statement-feedback-column');
      feedbackColumns[1].hidden = true;
      feedbackColumns[1].style.display = 'none';
    } else {
      wrapper
        .querySelectorAll(
          '.ai-two-column-right-heading, .ai-two-column-right-spacer, .ai-two-column-right-body',
        )
        .forEach((el) => {
          el.hidden = true;
          el.style.display = 'none';
        });
    }

    if (this.hasAssistantTextareaTarget) {
      this.assistantTextareaTarget.value = '';
    }
  }

  removeFeedbackUiIfPresent() {
    this.element.querySelectorAll('.ai-assistant-text-input-post-response-ui').forEach((el) => {
      el.remove();
    });
    this.element
      .querySelectorAll('[data-ai-assistant-text-input-target="feedbackContainer"]')
      .forEach((el) => {
        el.remove();
      });
  }

  closeErrorModal() {
    this.closeModalElement(this.errorModalTarget);
    if (this.isDesignSystemCaseExperience() && (this._responseError || this.responseErrorValue)) {
      this.applyDesignSystemOneColumnErrorFallback();
      void this.finalizeAnswerCommentInPlaceholder();
    }
    this.syncContinueButtonState();
  }

  async persistGenericAiComponentResponseError(message) {
    if (!this.documentIdValue || !this.userIdValue) return;

    await updateDocumentUserState({
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      body: {
        document_user_state: {
          state: {
            [normalizeNodeUid(this.nodeUidValue)]: {
              genericAiComponentResponseError: {
                value: message || true,
                updatedAt: Date.now(),
              },
            },
          },
        },
      },
    });
  }

  selectRating = (event) => {
    if (this.feedbackSubmittedValue) return;
    const input = event.currentTarget;
    const rating = Number(input?.dataset?.rating) || Number(input?.value);
    if (!rating) return;
    this._selectedRating = rating;
    this.updateFeedbackSubmitButtonState();
  };

  submitFeedbackRating = (event) => {
    event?.preventDefault?.();
    void this.submitThreadFeedback();
  };

  getActiveFeedbackSubmitButtons() {
    return this.feedbackSubmitButtonTargets.filter((btn) => !btn.closest('template'));
  }

  getActiveRatingButtons() {
    return this.ratingButtonTargets.filter((btn) => !btn.closest('template'));
  }

  getActiveFeedbackComments() {
    return this.feedbackCommentTargets.filter((el) => !el.closest('template'));
  }

  restoreFeedbackSelectionOnConnect() {
    const rating = Number(this.feedbackUserRatingValue) || 0;
    const comment = this.feedbackUserCommentValue || '';

    if (rating > 0) {
      this._selectedRating = rating;
      this.getActiveRatingButtons().forEach((input) => {
        const inputRating = Number(input.dataset.rating) || Number(input.value);
        input.checked = inputRating === rating;
      });
    }

    if (comment) {
      this.getActiveFeedbackComments().forEach((textarea) => {
        textarea.value = comment;
      });
    }
  }

  configureFeedbackSubmitButton() {
    this.restoreFeedbackSelectionOnConnect();
    if (!this.feedbackSubmittedValue && this._selectedRating <= 0) {
      this._selectedRating = 0;
    }
    this.updateFeedbackSubmitButtonState();
  }

  updateFeedbackSubmitButtonState() {
    const buttons = this.getActiveFeedbackSubmitButtons();
    if (!buttons.length) return;

    const disabled = this.feedbackSubmittedValue || this._selectedRating <= 0;
    buttons.forEach((btn) => {
      btn.disabled = disabled;
      btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    });
  }

  async submitThreadFeedback() {
    if (this.feedbackSubmittedValue || this._selectedRating <= 0 || this._feedbackSubmitInFlight) {
      return;
    }
    const threadId = this.getResolvedThreadId();
    if (!threadId) return;
    const comment = this.hasFeedbackCommentTarget ? this.feedbackCommentTarget.value : '';
    const nodeKey = normalizeNodeUid(this.nodeUidValue);
    const now = Date.now();

    this._feedbackSubmitInFlight = true;
    this.getActiveFeedbackSubmitButtons().forEach((btn) => {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
    });

    try {
      await submitFeedback(threadId, this._selectedRating, [], comment);
      this.feedbackSubmittedValue = true;
      this.feedbackUserRatingValue = this._selectedRating;
      this.feedbackUserCommentValue = comment;
      await updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body: {
          document_user_state: {
            state: {
              [nodeKey]: {
                genericAiComponentFeedbackSubmitted: { value: true, updatedAt: now },
                genericAiComponentUserRating: { value: this._selectedRating, updatedAt: now },
                genericAiComponentUserComment: { value: comment, updatedAt: now },
              },
            },
          },
        },
      });
      this.disableFeedbackInputs();
      this.syncContinueButtonState();
    } catch (e) {
      if (e?.status !== 401) {
        registerErrorInRollbar('AI assistant text input feedback submission failed', { error: e });
      }
      this.updateFeedbackSubmitButtonState();
    } finally {
      this._feedbackSubmitInFlight = false;
    }
  }

  populateAssistantFromMessages(msgs) {
    const assistant = [...(msgs || [])].reverse().find((m) => String(m.role) === 'assistant');
    const user = [...(msgs || [])].find((m) => String(m.role) === 'user');
    if (user && this.hasUserDisplayTextareaTarget) {
      this.userDisplayTextareaTarget.value = String(user.content || '');
    }
    const assistantText = String(assistant?.content || '').trim();
    if (this.assistantResponseComplete(msgs)) {
      this.showAssistantResponse(assistantText);
    } else if (this.assistantResponsePending(msgs)) {
      this.showAssistantLoading();
    }
  }

  assistantResponsePending(msgs = this.messagesValue) {
    if (!this.responseSubmittedValue && !this._awaitingAssistantResponse) return false;
    if (this.assistantResponseComplete(msgs)) return false;

    const list = Array.isArray(msgs) ? msgs : [];
    const assistant = [...list].reverse().find((m) => String(m.role) === 'assistant');
    const assistantText = String(assistant?.content || '').trim();
    if (assistant && (assistant.submission_stage === 'error' || assistantText.includes(FEEDBACK_BAD_PHRASE))) {
      return false;
    }
    return true;
  }

  showAssistantLoading() {
    const textarea = this.findAssistantTextarea();
    if (!textarea) return;
    textarea.value = ASSISTANT_LOADING_TEXT;
  }

  showAssistantResponse(text) {
    const textarea = this.findAssistantTextarea();
    if (!textarea) return;
    textarea.value = text;
  }

  maybeRevealFeedbackUi() {
    if (this._responseError || this.responseErrorValue) return;
    if (!this.vpeEnabledValue || vpeChatErrorBlocksFeedback(getDusFromDom())) return;
    this.revealPreResponseFeedbackBlock();
    if (this.element.querySelector('[data-ai-assistant-text-input-target="feedbackContainer"]')) {
      this.moveFeedbackBelowAnswerComment();
      requestAnimationFrame(() => {
        if (this.element.isConnected) this.configureFeedbackSubmitButton();
      });
      return;
    }

    const template = this.hasFeedbackTemplateTarget
      ? this.feedbackTemplateTarget
      : this.element.querySelector('[data-ai-assistant-text-input-target="feedbackTemplate"]');
    const placeholder = this.getAnswerCommentPlaceholder();
    if (!template?.content || !placeholder) return;

    placeholder.after(template.content.cloneNode(true));
    this.moveFeedbackBelowAnswerComment();
    requestAnimationFrame(() => {
      if (this.element.isConnected) this.configureFeedbackSubmitButton();
    });
  }


  usesSingleColumnLayout() {
    return !this.vpeEnabledValue || this._responseError || this.responseErrorValue;
  }

  usesTwoColumnLayout() {
    return this.vpeEnabledValue && !this._responseError && !this.responseErrorValue;
  }

  isDesignSystemCaseExperience() {
    return this.element.classList.contains('doc-case-experience--generic-ai-component');
  }

  markDesignSystemSubmitButtonSubmitted() {
    if (!this.isDesignSystemCaseExperience()) return;

    if (this.hasSubmittedButtonTemplateTarget && this.hasSubmitPlaceholderTarget) {
      this.submitPlaceholderTarget.innerHTML = this.submittedButtonTemplateTarget.innerHTML;
      this.submitPlaceholderTarget.hidden = false;
      this.submitPlaceholderTarget.style.removeProperty('display');
      return;
    }

    const button = this.hasSubmitButtonTarget
      ? this.submitButtonTarget
      : this.element.querySelector('.ai-dynamic-submit');
    if (!button) return;

    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');

    const label = button.querySelector('.btn-label');
    if (label) label.textContent = 'Submitted';
    button.classList.add('generic-ai-component-submit--submitted');

    if (this.hasSubmitPlaceholderTarget) {
      this.submitPlaceholderTarget.hidden = false;
      this.submitPlaceholderTarget.style.removeProperty('display');
    }
  }

  lockDesignSystemUserInput() {
    if (this.hasUserTextareaTarget) this.userTextareaTarget.disabled = true;
    if (this.hasUserDisplayTextareaTarget) this.userDisplayTextareaTarget.disabled = true;
    removeSpeechToTextToolbarFromContainer(this.element);
    this.markDesignSystemSubmitButtonSubmitted();
  }

  removeTwoColumnLayoutIfPresent() {
    this.element.querySelectorAll('.ai-two-column-wrapper').forEach((wrapper) => {
      wrapper.remove();
    });
    if (this.hasInputFormTarget) {
      this.inputFormTarget.hidden = false;
      this.inputFormTarget.style.display = '';
    }
    const placeholder = this.getAnswerCommentPlaceholder();
    if (placeholder) {
      placeholder.hidden = false;
      placeholder.style.display = '';
    }
  }

  applyDesignSystemOneColumnErrorFallback() {
    if (!this.isDesignSystemCaseExperience()) return;

    if (this.hasUserDisplayTextareaTarget && this.hasUserTextareaTarget) {
      const displayText = this.userDisplayTextareaTarget.value?.trim();
      if (displayText) {
        this.userTextareaTarget.value = displayText;
      }
    }

    this.element.querySelectorAll('.ai-two-column-wrapper').forEach((wrapper) => {
      wrapper.remove();
    });

    const contentHost = this.element.querySelector(
      `#generic-ai-component-stream-content-${this.nodeUidValue}`,
    );
    if (contentHost) {
      contentHost.innerHTML = '';
    }

    const feedbackBlock = this.element.querySelector('.generic-ai-component-feedback-block');
    if (feedbackBlock) {
      feedbackBlock.classList.add('generic-ai-component-feedback-block--pre-response');
      feedbackBlock.hidden = true;
      feedbackBlock.style.display = 'none';
    }

    if (this.hasInputFormTarget) {
      this.inputFormTarget.hidden = false;
      this.inputFormTarget.style.removeProperty('display');
    }

    if (this.hasUserTextareaTarget) {
      this.userTextareaTarget.disabled = true;
      this.userTextareaTarget.classList.add('form-textarea-disabled');
    }

    const placeholder = this.getAnswerCommentPlaceholder();
    if (placeholder) {
      placeholder.hidden = false;
      placeholder.style.removeProperty('display');
    }

    removeSpeechToTextToolbarFromContainer(this.element);
    this.markDesignSystemSubmitButtonSubmitted();
  }

  revealResponseStream(host) {
    if (!host) return;
    host.hidden = false;
    host.removeAttribute('aria-hidden');
    host.style.removeProperty('display');
  }

  revealPreResponseFeedbackBlock() {
    const block = this.element.querySelector('.generic-ai-component-feedback-block--pre-response');
    if (!block) return;
    block.classList.remove('generic-ai-component-feedback-block--pre-response');
    block.hidden = false;
    block.style.removeProperty('display');
  }

  mountDesignSystemTwoColumnFromTemplate(contentHost, { userText, lines, limit }) {
    if (!this.hasTwoColumnTemplateTarget) return false;

    const fragment = this.twoColumnTemplateTarget.content.cloneNode(true);
    const wrapper = fragment.querySelector('.ai-two-column-wrapper');
    if (!wrapper) return false;

    const userDisplay = wrapper.querySelector(
      '[data-ai-assistant-text-input-target="userDisplayTextarea"]',
    );
    if (userDisplay) {
      userDisplay.value = userText;
      userDisplay.rows = lines;
    }

    const assistant = wrapper.querySelector(
      '[data-ai-assistant-text-input-target="assistantTextarea"]',
    );
    if (assistant) {
      assistant.value = ASSISTANT_LOADING_TEXT;
      assistant.rows = lines;
    }

    const counters = wrapper.querySelectorAll('.form-input-counter');
    if (counters[0]) counters[0].textContent = `${userText.length}/${limit}`;
    if (counters[1]) counters[1].textContent = `0/${limit}`;

    contentHost.replaceChildren(wrapper);
    return true;
  }

  showTwoColumnLayout() {
    if (!this.usesTwoColumnLayout()) return;
    if (!this.hasResponseStreamTarget && !this.hasVisibleUiTarget) return;

    const host = this.hasResponseStreamTarget ? this.responseStreamTarget : this.visibleUiTarget;
    this.revealResponseStream(host);

    const contentHost =
      host.querySelector(`#generic-ai-component-stream-content-${this.nodeUidValue}`) || host;

    const existing = contentHost.querySelector('.ai-two-column-wrapper');
    if (existing) {
      existing.style.display = '';
      this.hideInputForm();
      return;
    }

    const userText = this.userTextareaTarget?.value || '';
    const lines = this.boxLinesValue || 5;
    const limit = this.characterLimitValue || 1000;
    if (this.isDesignSystemCaseExperience()) {
      if (!this.mountDesignSystemTwoColumnFromTemplate(contentHost, { userText, lines, limit })) {
        return;
      }
    } else {
      contentHost.innerHTML = `
      <div class="ai-two-column-wrapper ai-summary-feedback-block">
      <h3 class="ai-two-column-left-heading">Your Response</h3>
      <h3 class="ai-two-column-right-heading">AI-Generated Feedback</h3>
      <div class="ai-two-column-right-spacer" aria-hidden="true"></div>
      <div class="ai-two-column-left-body">
        <textarea rows="${lines}" class="doc-text-input-field" disabled data-ai-assistant-text-input-target="userDisplayTextarea"></textarea>
        <span class="letter-count">Letter Count: ${userText.length} / ${limit}</span>
      </div>
      <div class="ai-two-column-right-body">
        <textarea rows="${lines}" class="ai-feedback-box doc-text-input-field" disabled
          data-ai-assistant-text-input-target="assistantTextarea">${ASSISTANT_LOADING_TEXT}</textarea>
      </div>
      </div>`;
    }
    if (this.hasUserDisplayTextareaTarget) {
      this.userDisplayTextareaTarget.value = userText;
    }
    this.hideInputForm();
  }

  findAnswerCommentElement() {
    const parent = this.element.parentElement;
    if (parent) {
      for (const child of parent.children) {
        if (child === this.element) continue;
        if (
          child.classList?.contains('doc-section') &&
          child.classList.contains('answer_comment')
        ) {
          return child;
        }
      }
    }

    return null;
  }

  getNotesController() {
    return (
      this.application.getControllerForElementAndIdentifier(this.element, 'case-experience--notes') ||
      this.application.getControllerForElementAndIdentifier(this.element, 'notes')
    );
  }

  ensureAnswerCommentStored() {
    if (this.answerCommentHtml) return;

    const notesController = this.getNotesController();
    if (notesController?.answerCommentHtml) {
      this.answerCommentHtml = notesController.answerCommentHtml;
      return;
    }

    const existing = this.findAnswerCommentElement();
    if (existing) {
      const snapshot = snapshotAnswerCommentSiblingHtml(this.element);
      if (snapshot) {
        this.answerCommentHtml = snapshot.html;
        return;
      }
      this.answerCommentHtml = existing.outerHTML;
      return;
    }

    const extracted = extractHiddenAnswerCommentHTML(this.element);
    if (extracted) {
      this.answerCommentHtml = extracted.html;
      return;
    }

    const removed = extractAnswerCommentSiblingHtml(this.element);
    if (removed) {
      this.answerCommentHtml = removed.html;
      return;
    }

    if (this.hasAnswerCommentBlockTemplateTarget) {
      const templateHtml = this.answerCommentBlockTemplateTarget.innerHTML?.trim();
      if (templateHtml) {
        this.answerCommentHtml = templateHtml;
      }
    }
  }

  resolveAnswerCommentId() {
    const existing = this.findAnswerCommentElement();
    if (existing?.id) return existing.id;

    this.ensureAnswerCommentStored();
    const html = this.answerCommentHtml || this.getNotesController()?.answerCommentHtml;
    if (!html) return null;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('.answer_comment')?.id || null;
  }

  unhideAnswerCommentElement(answerCommentEl) {
    const unhide = (el) => {
      el.classList?.remove('hidden', 'collapsed');
      for (const child of el.children || []) {
        unhide(child);
      }
    };
    unhide(answerCommentEl);
    answerCommentEl.style.removeProperty('display');
    void answerCommentEl.offsetHeight;
  }

  async revealAnswerComment() {
    const parent = this.element.parentElement;
    if (!parent) return;

    this.ensureAnswerCommentStored();

    let existing = this.findAnswerCommentElement();
    if (!existing) {
      const html = this.answerCommentHtml || this.getNotesController()?.answerCommentHtml;
      restoreAnswerCommentHTML(this.element, html);
      existing = this.findAnswerCommentElement();
    }

    if (existing) {
      this.unhideAnswerCommentElement(existing);
      this.placeAnswerCommentInPlaceholder();
      this.suppressEmptyExternalAnswerCommentPlaceholder();
      void this.persistAnswerCommentRevealedIfNeeded();
    }
  }

  hideInputForm() {
    if (!this.hasInputFormTarget) return;
    this.inputFormTarget.hidden = true;
    this.inputFormTarget.style.display = 'none';
    if (this.isDesignSystemCaseExperience()) {
      this.markDesignSystemSubmitButtonSubmitted();
    }
  }

  lockUserInput() {
    if (this.isDesignSystemCaseExperience()) {
      this.lockDesignSystemUserInput();
      return;
    }

    if (this.usesSingleColumnLayout()) {
      if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
      if (this.hasUserTextareaTarget) this.userTextareaTarget.disabled = true;
      if (this.hasSubmitPlaceholderTarget) this.submitPlaceholderTarget.style.display = 'none';
      return;
    }

    this.hideInputForm();
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
    if (this.hasUserTextareaTarget) this.userTextareaTarget.disabled = true;
    if (this.hasSubmitPlaceholderTarget) this.submitPlaceholderTarget.style.display = 'none';
  }

  disableFeedbackInputs() {
    this.ratingButtonTargets.forEach((btn) => {
      btn.disabled = true;
    });
    if (this.hasFeedbackCommentTarget) this.feedbackCommentTarget.disabled = true;
    this.getActiveFeedbackSubmitButtons().forEach((btn) => {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      const label = btn.querySelector('.btn-label');
      if (label) label.textContent = 'Submitted';
      btn.classList.add('generic-ai-component-submit--submitted');
    });
  }

  scheduleResponseSafetyTimeout() {
    clearTimeout(this._responseSafetyTimeout);
    this._responseSafetyTimeout = setTimeout(() => {
      if (!this._awaitingAssistantResponse) return;
      if (this.assistantResponseComplete() && !this.isAssistantErrorResponse()) {
        void this.onAssistantResponseReady();
        return;
      }
      void this.recoverFromAiResponseError('Connection Timeout');
    }, RESPONSE_SAFETY_TIMEOUT_MS);
  }

  getResolvedThreadId() {
    if (this.hasThreadIdValue && this.threadIdValue) return String(this.threadIdValue);
    return null;
  }

  async persistThreadIdToDus(threadId) {
    if (!threadId || !this.documentIdValue || !this.userIdValue) return;
    if (this._threadIdPersistedToDus === String(threadId)) return;

    this._threadIdPersistedToDus = String(threadId);
    try {
      await updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body: {
          document_user_state: {
            state: {
              [normalizeNodeUid(this.nodeUidValue)]: {
                threadId: { value: threadId, updatedAt: Date.now() },
              },
            },
          },
        },
      });
    } catch {
      this._threadIdPersistedToDus = null;
    }
  }

  async syncMessagesToDus() {
    const threadId = this.getResolvedThreadId();
    if (!threadId || !this.documentIdValue || !this.userIdValue) return false;

    try {
      const res = await fetch(
        `/ai_assistant/messages/sync_from_thread?thread_id=${encodeURIComponent(threadId)}`,
        {
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
        },
      );
      if (!res.ok) return false;
      const data = await res.json();
      this.threadIdValue = data.thread_id;
      this.messagesValue = data.messages;
      this._threadIdPersistedToDus = String(data.thread_id);
      await updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body: {
          document_user_state: {
            state: {
              [normalizeNodeUid(this.nodeUidValue)]: {
                threadId: { value: data.thread_id, updatedAt: Date.now() },
                messages: { value: data.messages, updatedAt: Date.now() },
              },
            },
          },
        },
      });
      return true;
    } catch {
      return false;
    }
  }

  async persistGraded() {
    const content = this.userTextareaTarget?.value || '';
    await updateDocumentUserState({
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      body: {
        document_user_state: {
          state: {
            [normalizeNodeUid(this.nodeUidValue)]: {
              graded: { value: true, updatedAt: Date.now() },
              value: { value: content, updatedAt: Date.now() },
            },
          },
        },
      },
    });
  }

  getCardContinueButton() {
    let node = this.element;
    while (node) {
      const section =
        node.closest('[data-case-experience--case-viewer-target="section"]') ||
        node.closest('[data-case-viewer-target="section"]');
      if (!section) break;

      const continueBtn =
        section.querySelector('[data-case-experience--case-viewer-target="continueButton"]') ||
        section.querySelector('[data-case-viewer-target="continueButton"]');
      if (continueBtn) return continueBtn;

      node = section.parentElement;
    }
    return null;
  }

  continueInactiveAttribute(continueBtn) {
    if (
      continueBtn.hasAttribute('data-case-experience--button-action-inactive-value') ||
      (continueBtn.getAttribute('data-controller') || '').includes('case-experience--button-action')
    ) {
      return 'data-case-experience--button-action-inactive-value';
    }
    return 'data-button-action-inactive-value';
  }

  syncContinueButtonState() {
    const continueBtn = this.getCardContinueButton();
    if (!continueBtn) return;

    const inactiveAttr = this.continueInactiveAttribute(continueBtn);

    if (this._responseError || this.responseErrorValue) {
      continueBtn.setAttribute(inactiveAttr, '1');
      this._refreshContinueButton(continueBtn);
      return;
    }

    const dus = getDusFromDom();
    const vpeOffOrError = !this.vpeEnabledValue || vpeChatErrorBlocksFeedback(dus);

    if (vpeOffOrError) {
      if (this.responseSubmittedValue || !this.vpeEnabledValue) {
        continueBtn.setAttribute(inactiveAttr, '1');
      }
      this._refreshContinueButton(continueBtn);
      return;
    }

    if (!this.responseSubmittedValue) {
      continueBtn.setAttribute(inactiveAttr, '2');
    } else if (this.feedbackSubmittedValue) {
      continueBtn.setAttribute(inactiveAttr, '1');
    } else {
      continueBtn.setAttribute(inactiveAttr, '2');
    }
    this._refreshContinueButton(continueBtn);
    syncAllContinueButtonsForDocument(dus);
  }

  _refreshContinueButton(continueBtn) {
    const controllerIds = ['case-experience--button-action', 'button-action'];
    for (const identifier of controllerIds) {
      const buttonController = this.application.getControllerForElementAndIdentifier(
        continueBtn,
        identifier,
      );
      if (!buttonController?.updateButtonState) continue;

      const inactiveAttr = this.continueInactiveAttribute(continueBtn);
      const rawInactive = continueBtn.getAttribute(inactiveAttr);
      if (rawInactive != null && rawInactive !== '') {
        const inactive = Number(rawInactive);
        if (Number.isFinite(inactive)) {
          buttonController.inactiveValue = inactive;
        }
      }
      buttonController.updateButtonState();
      return;
    }
  }
}
